import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { renameFolder, RenameFolderModel } from "client/Api/Email/email";
import { useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";

const validationConfiguration: IValidationConfiguration<RenameFolderModel> = createValidationConfiguration<RenameFolderModel>();
validationConfiguration.forScalar("newFolderName")
	.add(
		isNotNullNorUndefined().activateOn("formSubmit"),
		isNotEmpty().activateOn("formSubmit")
	);

export type EmailRenameFolderDialogProps = {
	smtpAddress: string;
	folderId: string;
	folderName: string;
	onRenameFolder(newName: string): void;
	onClose(): void;
};

export const EmailRenameFolderDialog = (props: EmailRenameFolderDialogProps) => {
	const {
		onRenameFolder,
		onClose,
		smtpAddress,
		folderId,
		folderName,
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [model, setModel] = useState<RenameFolderModel>({
		location: {
			smtpAddress: {
				isSet: true,
				value: smtpAddress
			},
			folderId: folderId
		},
		newFolderName: folderName
	});
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<RenameFolderModel> =
		useFormHandler(
			model,
			setModel,
			validationConfiguration
		);

	const confirmHandler = async (): Promise<void> => {
		try {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);
			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}
			setIsLoading(true);

			await renameFolder(
				model,
				globalCancellation
			);

			setIsLoading(false);
			onRenameFolder(model.newFolderName);
			onClose();
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsLoading(false);
		}
	};

	return (
		<Dialog
			trackingId="email-rename-folder-dialog"
			hasBusyIndicator={isLoading}
		>
			<Dialog.Header
				title={localize("_Rename")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={confirmHandler}>
					<FormItem
						isRequired
						hasNoMargin
					>
						<TextInput
							{...formHandler.for("newFolderName").attachToTextInput()}
							autoFocus="select"
						/>
						<ValidationError {...formHandler.for("newFolderName").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={confirmHandler}
						text={localize("_Done")}
						trackingId="confirm"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

EmailRenameFolderDialog.displayName = "EmailRenameFolderDialog";

export type EmailRenameFolderDialogInitialData = Omit<EmailRenameFolderDialogProps, "onClose" | "onRenameFolder">;

export function useEmailRenameFolderDialog() {
	return useDataInputDialog<EmailRenameFolderDialogInitialData, string>(
		(initialData, onClose, onRenameFolder) => (
			<EmailRenameFolderDialog
				{...initialData}
				onClose={onClose}
				onRenameFolder={onRenameFolder}
			/>
		)
	);
}